import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MdArrowBack, 
  MdSave, 
  MdInventory,
  MdCategory,
  MdNumbers,
  MdDateRange,
  MdGrade,
  MdGridOn,
  MdViewModule,
  MdClose,
  MdCheck,
  MdStorage,
  MdSpaceBar,
  MdWarning
} from 'react-icons/md';
import UsibrasLoader from '../../../components/UsibrasLoader';
import UsibrasToast from '../../../components/UsibrasToast';

const AddFinProduct = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    productType: '',
    rcnType: '',
    date: '',
    julianDate: '',
    quantity: '',
    lane: '',
    slot: ''
  });
  const [errors, setErrors] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const [slotData, setSlotData] = useState(null);
  const [loadingSlotData, setLoadingSlotData] = useState(false);
  const [fullSlots, setFullSlots] = useState([]);
  const [loadingLaneAvailability, setLoadingLaneAvailability] = useState(false);

  // RCN types for dropdown
  const rcnTypes = [
    'Organic', 'Fair trade', 'Conventional', 'BK'
  ];

  // Product types for dropdown
  const productTypes = [
    'WW 450', 'W2 450', 'W2 180', 'CW 210', 'WW 210', 'CW 360', 'WW 360', 'W2 210', 'WW 240',
    'WW 320', 'WW 240s', 'CW 320', 'W2 240', 'W2 320', 'SK1', 'WS', 'B1', 'W320S', 'RAW CASHEW BUTTER',
    'ROASTED CASHEW BUTTER', 'W2 360', 'W3', 'SK2', 'SK3', 'SP1 BAG', 'W3M', 'B2', 'S1', 'S2',
    'S3', 'P1', 'P2', 'P3', 'SP1', 'SP2', 'SP3', 'SSP1', 'SSP2', 'SSP3',
    'X3', 'X2', 'G2', 'F3', 'W2 360', 'BK'
  ];

  // Generate lane options (1-8)
  const generateLaneOptions = () => {
    const lanes = [];
    for (let i = 1; i <= 8; i++) {
      lanes.push(`Lane ${i}`);
    }
    return lanes;
  };

  const laneOptions = generateLaneOptions();
  
  // Function to calculate Julian date from regular date
  const calculateJulianDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const year = date.getFullYear();
    const startOfYear = new Date(year, 0, 1);
    const diffTime = date - startOfYear;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    
    // Format as DDDYY (3-digit day of year + 2-digit year)
    const yearShort = year.toString().slice(-2);
    const dayOfYear = diffDays.toString().padStart(3, '0');
    
    return `${dayOfYear}${yearShort}`;
  };
  
  // Generate slot options based on selected lane
  const generateSlotOptions = (selectedLane) => {
    if (!selectedLane) return [];
    
    const laneNumber = parseInt(selectedLane.replace(/\D/g, ''));
    const slots = [];
    
    // Lane 1 and 7: A1 to A54
    if (laneNumber === 1 || laneNumber === 7) {
      for (let i = 1; i <= 54; i++) {
        slots.push(`A${i}`);
      }
    } else {
      // Other lanes: A1 to A66 and B1 to B66
      for (let i = 1; i <= 66; i++) {
        slots.push(`A${i}`);
      }
      for (let i = 1; i <= 66; i++) {
        slots.push(`B${i}`);
      }
    }
    
    return slots;
  };

  // Fetch lane availability data to get full slots
  const fetchLaneAvailability = async (lane) => {
    if (!lane) return;
    
    setLoadingLaneAvailability(true);
    try {
      const response = await fetch(`https://usibras-web-backend.onrender.com/api/lane-availability/${encodeURIComponent(lane)}`);
      const data = await response.json();
      
      if (response.ok) {
        // Extract slot names from full_slots array
        const fullSlotNames = data.full_slots.map(item => item.slot);
        setFullSlots(fullSlotNames);
      } else {
        console.error('Error fetching lane availability:', data);
        setFullSlots([]);
      }
    } catch (error) {
      console.error('Error fetching lane availability:', error);
      setFullSlots([]);
    } finally {
      setLoadingLaneAvailability(false);
    }
  };

  // Fetch real slot data from API
  const fetchSlotData = async (lane, slot) => {
    if (!lane || !slot) return;
    
    setLoadingSlotData(true);
    try {
      const formattedLane = lane;
      const response = await fetch(`https://usibras-web-backend.onrender.com/api/space-availability/${encodeURIComponent(formattedLane)}/${encodeURIComponent(slot)}`);
      const data = await response.json();
      
      if (response.ok) {
        const maxCapacity = 72;
        const usedSpace = parseInt(data.total_actual_stock) || 0;
        const availableSpace = maxCapacity - usedSpace;
        const utilizationPercentage = Math.round((usedSpace / maxCapacity) * 100);
        
        setSlotData({
          maxCapacity,
          usedSpace,
          availableSpace,
          utilizationPercentage,
          status: availableSpace > 20 ? 'available' : availableSpace > 5 ? 'limited' : 'full',
          products: data.products || []
        });
      } else {
        setSlotData({
          maxCapacity: 72,
          usedSpace: 0,
          availableSpace: 72,
          utilizationPercentage: 0,
          status: 'available',
          products: []
        });
      }
    } catch (error) {
      console.error('Error fetching slot data:', error);
      setSlotData({
        maxCapacity: 72,
        usedSpace: 0,
        availableSpace: 72,
        utilizationPercentage: 0,
        status: 'available',
        products: []
      });
    } finally {
      setLoadingSlotData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Auto-calculate Julian date when regular date changes
    if (name === 'date') {
      const julianDate = calculateJulianDate(value);
      setFormData(prev => ({
        ...prev,
        julianDate: julianDate
      }));
    }
    
    // Clear slot when lane changes
    if (name === 'lane') {
      setFormData(prev => ({
        ...prev,
        slot: ''
      }));
      setSlotData(null);
      setFullSlots([]);
      
      // Fetch lane availability data for the selected lane
      if (value) {
        fetchLaneAvailability(value);
      }
    }
    
    // Fetch slot data when both lane and slot are selected
    if ((name === 'lane' && value && formData.slot) || (name === 'slot' && value && formData.lane)) {
      const laneToUse = name === 'lane' ? value : formData.lane;
      const slotToUse = name === 'slot' ? value : formData.slot;
      fetchSlotData(laneToUse, slotToUse);
    }
    
    // Real-time quantity validation
    if (name === 'quantity' && value) {
      const quantityValue = parseInt(value);
      
      if (quantityValue <= 0) {
        setToast({
          show: true,
          message: 'Quantity must be greater than 0',
          type: 'error'
        });
        setErrors(prev => ({
          ...prev,
          quantity: 'Quantity must be greater than 0'
        }));
        return;
      }
      
      if (slotData && quantityValue > slotData.availableSpace) {
        setToast({
          show: true,
          message: `Quantity exceeds available space. Only ${slotData.availableSpace} boxes can be stored in this slot.`,
          type: 'error'
        });
        setErrors(prev => ({
          ...prev,
          quantity: `Maximum ${slotData.availableSpace} boxes allowed`
        }));
      } else {
        if (errors.quantity) {
          setErrors(prev => ({
            ...prev,
            quantity: ''
          }));
        }
      }
    }
    
    // Clear error when user starts typing (for other fields)
    if (errors[name] && name !== 'quantity') {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.productType) newErrors.productType = 'Product type is required';
    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.quantity) newErrors.quantity = 'Quantity is required';
    if (formData.quantity && isNaN(formData.quantity)) newErrors.quantity = 'Quantity must be a number';
    if (formData.quantity && parseInt(formData.quantity) <= 0) newErrors.quantity = 'Quantity must be greater than 0';
    if (!formData.lane) newErrors.lane = 'Lane is required';
    if (!formData.slot) newErrors.slot = 'Slot is required';

    if (formData.quantity && slotData) {
      if (parseInt(formData.quantity) > slotData.availableSpace) {
        setToast({
          show: true,
          message: `Quantity exceeds available space. Only ${slotData.availableSpace} boxes can be stored in this slot.`,
          type: 'error'
        });
        newErrors.quantity = `Maximum ${slotData.availableSpace} boxes allowed`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setShowPreview(true);
    }
  };

  const handleConfirmSubmit = async () => {
    setIsLoading(true);
    
    if (!formData.productType || !formData.date || !formData.quantity || !formData.lane || !formData.slot) {
      setToast({
        show: true,
        message: 'Please fill in all required fields',
        type: 'error'
      });
      setIsLoading(false);
      return;
    }
    
    const payload = {
      product_type: formData.productType,
      rcn_type: formData.rcnType,
      date: formData.date,
      julian_date: formData.julianDate,
      quantity: parseInt(formData.quantity),
      lane: formData.lane,
      slot: formData.slot
    };
    
    if (isNaN(payload.quantity) || payload.quantity <= 0) {
      setToast({
        show: true,
        message: 'Invalid quantity value',
        type: 'error'
      });
      setIsLoading(false);
      return;
    }
    
    if (!payload.slot || payload.slot.trim() === '') {
      setToast({
        show: true,
        message: 'Invalid slot value',
        type: 'error'
      });
      setIsLoading(false);
      return;
    }
    
    console.log('Sending payload:', payload);
    
    try {
      const response = await fetch('https://usibras-web-backend.onrender.com/api/add-finished-product', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (response.ok) {
        setToast({
          show: true,
          message: 'Product added successfully!',
          type: 'success'
        });
        setFormData({
          productType: '',
          rcnType: '',
          date: '',
          julianDate: '',
          quantity: '',
          lane: '',
          slot: ''
        });
        setShowPreview(false);
        setIsLoading(false);
      } else {
        console.error('Server error:', data);
        setToast({
          show: true,
          message: data.message || data.error || `Server error: ${response.status}`,
          type: 'error'
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setToast({
        show: true,
        message: `Network error: ${error.message}`,
        type: 'error'
      });
      setIsLoading(false);
    }
  };

  const currentSlotData = slotData;

  return (
    <div className="mt-5 p-4">
      {toast.show && (
        <UsibrasToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(prev => ({ ...prev, show: false }))}
          position="top-right"
          duration={3000}
        />
      )}

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transform hover:scale-105 transition-all duration-200"
          >
            <div className="bg-blue-50 p-2 rounded-lg transform rotate-3 shadow-md">
              <MdArrowBack size={24} />
            </div>
            <span className="font-medium">Back to Products</span>
          </button>
        </div>
      </div>

      <div className="mb-8">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-lg">
          <div className="flex items-center space-x-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-700 p-4 rounded-lg transform rotate-3 shadow-lg">
              <MdInventory className="text-white" size={32} />
            </div>
            <h1 className="text-3xl font-bold text-gray-800">Add New Finished Product</h1>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
            <MdStorage className="mr-2 text-blue-500" size={24} />
            Product Information
          </h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <MdCategory className="text-blue-500" size={20} />
                    <span>Product Type</span>
                  </div>
                </label>
                <select
                  name="productType"
                  value={formData.productType}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm ${
                    errors.productType ? 'border-red-300' : 'border-gray-300'
                  } focus:ring-blue-500 focus:border-blue-500`}
                >
                  <option value="">Select Product Type</option>
                  {productTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                {errors.productType && (
                  <p className="mt-1 text-sm text-red-600">{errors.productType}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <MdCategory className="text-blue-500" size={20} />
                    <span>RCN Type</span>
                  </div>
                </label>
                <select
                  name="rcnType"
                  value={formData.rcnType}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm ${
                    errors.rcnType ? 'border-red-300' : 'border-gray-300'
                  } focus:ring-blue-500 focus:border-blue-500`}
                >
                  <option value="">Select RCN Type</option>
                  {rcnTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                {errors.rcnType && (
                  <p className="mt-1 text-sm text-red-600">{errors.rcnType}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <MdDateRange className="text-blue-500" size={20} />
                    <span>Date</span>
                  </div>
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm ${
                    errors.date ? 'border-red-300' : 'border-gray-300'
                  } focus:ring-blue-500 focus:border-blue-500`}
                />
                {errors.date && (
                  <p className="mt-1 text-sm text-red-600">{errors.date}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <MdDateRange className="text-blue-500" size={20} />
                    <span>Julian Date</span>
                  </div>
                </label>
                <input
                  type="text"
                  name="julianDate"
                  value={formData.julianDate}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm ${
                    errors.julianDate ? 'border-red-300' : 'border-gray-300'
                  } focus:ring-blue-500 focus:border-blue-500`}
                  placeholder="Auto-generated from date"
                  maxLength="5"
                />
                {errors.julianDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.julianDate}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <MdNumbers className="text-blue-500" size={20} />
                    <span>Quantity(Boxes)</span>
                  </div>
                </label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  min="1"
                  className={`mt-1 block w-full rounded-md shadow-sm ${
                    errors.quantity ? 'border-red-300' : 'border-gray-300'
                  } focus:ring-blue-500 focus:border-blue-500`}
                  placeholder="Enter quantity"
                />
                {errors.quantity && (
                  <p className="mt-1 text-sm text-red-600">{errors.quantity}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <MdGridOn className="text-blue-500" size={20} />
                    <span>Lane</span>
                  </div>
                </label>
                <select
                  name="lane"
                  value={formData.lane}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm ${
                    errors.lane ? 'border-red-300' : 'border-gray-300'
                  } focus:ring-blue-500 focus:border-blue-500`}
                >
                  <option value="">Select Lane</option>
                  {laneOptions.map((lane) => (
                    <option key={lane} value={lane}>
                      {lane}
                    </option>
                  ))}
                </select>
                {errors.lane && (
                  <p className="mt-1 text-sm text-red-600">{errors.lane}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <MdViewModule className="text-blue-500" size={20} />
                    <span>Slot</span>
                  </div>
                </label>
                <select
                  name="slot"
                  value={formData.slot}
                  onChange={handleChange}
                  disabled={!formData.lane || loadingLaneAvailability}
                  className={`mt-1 block w-full rounded-md shadow-sm ${
                    errors.slot ? 'border-red-300' : 'border-gray-300'
                  } focus:ring-blue-500 focus:border-blue-500 ${!formData.lane || loadingLaneAvailability ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">
                    {loadingLaneAvailability ? 'Loading slots...' : formData.lane ? 'Select Slot' : 'Select Lane First'}
                  </option>
                  {generateSlotOptions(formData.lane).map((slot) => {
                    const isFull = fullSlots.includes(slot);
                    return (
                      <option 
                        key={slot} 
                        value={slot}
                        disabled={isFull}
                        className={isFull ? 'text-gray-400 bg-gray-100' : ''}
                      >
                        {slot} {isFull ? '(Full)' : ''}
                      </option>
                    );
                  })}
                </select>
                {errors.slot && (
                  <p className="mt-1 text-sm text-red-600">{errors.slot}</p>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transform hover:scale-105 transition-all duration-200"
              >
                <MdSave className="mr-2" size={20} />
                Save Product
              </button>
            </div>
          </form>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
            <MdSpaceBar className="mr-2 text-green-500" size={24} />
            Warehouse Space Availability
          </h2>
          
          {formData.lane ? (
            <div className="space-y-6">
              {loadingLaneAvailability && (
                <div className="flex justify-center items-center py-4">
                  <UsibrasLoader text="Loading lane availability..." color="blue" size="small" />
                </div>
              )}
              
              {!loadingLaneAvailability && fullSlots.length > 0 && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
                  <div className="flex items-center">
                    <MdWarning className="text-yellow-600 mr-3" size={20} />
                    <div>
                      <p className="text-yellow-800 font-medium">Full Slots Detected</p>
                      <p className="text-yellow-700 text-sm">
                        {fullSlots.length} slot{fullSlots.length !== 1 ? 's' : ''} in {formData.lane} are currently full and unavailable for storage.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {formData.slot && currentSlotData ? (
                <>
                  {loadingSlotData && (
                    <div className="flex justify-center items-center py-4">
                      <UsibrasLoader text="Loading space data..." color="blue" size="small" />
                    </div>
                  )}
                  
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-800 mb-2">Selected Location</h3>
                    <div className="flex items-center space-x-4">
                      <div className="bg-blue-500 text-white p-3 rounded-lg">
                        <MdGridOn size={24} />
                      </div>
                      <div>
                        <p className="text-blue-900 font-medium">{formData.lane}</p>
                        <p className="text-blue-700">{formData.slot}</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Total Capacity</p>
                          <p className="text-2xl font-bold text-gray-900">{currentSlotData.maxCapacity.toLocaleString()} boxes</p>
                        </div>
                        <div className="bg-blue-100 p-2 rounded-lg">
                          <MdStorage className="text-blue-600" size={24} />
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Space Available</p>
                          <p className="text-2xl font-bold text-green-600">{currentSlotData.availableSpace.toLocaleString()} boxes</p>
                        </div>
                        <div className="bg-green-100 p-2 rounded-lg">
                          <MdSpaceBar className="text-green-600" size={24} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Space Utilization</span>
                      <span className="text-sm font-medium text-gray-900">{currentSlotData.utilizationPercentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className={`h-3 rounded-full transition-all duration-500 ${
                          currentSlotData.status === 'available' ? 'bg-green-500' :
                          currentSlotData.status === 'limited' ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${currentSlotData.utilizationPercentage}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>0%</span>
                      <span>50%</span>
                      <span>100%</span>
                    </div>
                  </div>

                  <div className={`p-4 rounded-lg border-l-4 ${
                    currentSlotData.status === 'available' ? 'bg-green-50 border-green-400' :
                    currentSlotData.status === 'limited' ? 'bg-yellow-50 border-yellow-400' : 'bg-red-50 border-red-400'
                  }`}>
                    <div className="flex items-center">
                      <div className={`p-2 rounded-full ${
                        currentSlotData.status === 'available' ? 'bg-green-100' :
                        currentSlotData.status === 'limited' ? 'bg-yellow-100' : 'bg-red-100'
                      }`}>
                        {currentSlotData.status === 'available' ? (
                          <MdCheck className="text-green-600" size={20} />
                        ) : currentSlotData.status === 'limited' ? (
                          <MdWarning className="text-yellow-600" size={20} />
                        ) : (
                          <MdClose className="text-red-600" size={20} />
                        )}
                      </div>
                      <div className="ml-3">
                        <p className={`font-medium ${
                          currentSlotData.status === 'available' ? 'text-green-800' :
                          currentSlotData.status === 'limited' ? 'text-yellow-800' : 'text-red-800'
                        }`}>
                          {currentSlotData.status === 'available' ? 'Storage Available' :
                           currentSlotData.status === 'limited' ? 'Limited Storage' : 'Slot Full'}
                        </p>
                        <p className={`text-sm ${
                          currentSlotData.status === 'available' ? 'text-green-700' :
                          currentSlotData.status === 'limited' ? 'text-yellow-700' : 'text-red-700'
                        }`}>
                          {currentSlotData.status === 'available' ? 
                            `Can store ${currentSlotData.availableSpace.toLocaleString()} more boxes` :
                           currentSlotData.status === 'limited' ? 
                            `Can store ${currentSlotData.availableSpace.toLocaleString()} more boxes` :
                            'No space available in this slot'}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : formData.lane && !formData.slot ? (
                <div className="text-center py-12">
                  <div className="bg-gray-100 p-6 rounded-lg">
                    <MdViewModule className="mx-auto text-gray-400 mb-4" size={48} />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Slot</h3>
                    <p className="text-gray-500">
                      Choose a slot from the dropdown to view available space information.
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="bg-gray-100 p-6 rounded-lg">
                <MdStorage className="mx-auto text-gray-400 mb-4" size={48} />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select Lane & Slot</h3>
                <p className="text-gray-500">
                  Choose a lane and slot from the form to view available space information.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {showPreview && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => !isLoading && setShowPreview(false)}
            />

            <div className="relative w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl dark:bg-navy-800">
              <button
                onClick={() => !isLoading && setShowPreview(false)}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                disabled={isLoading}
              >
                <MdClose size={24} />
              </button>

              <div className="mb-6 flex items-center space-x-4">
                <div className="rounded-full bg-blue-50 p-3 dark:bg-navy-700">
                  <MdInventory className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Preview Product Details
                </h3>
              </div>

              {isLoading ? (
                <div className="flex justify-center items-center py-8">
                  <UsibrasLoader text="Saving Product..." color="blue" size="large" />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                      <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                        <MdCategory className="h-5 w-5" />
                        <span>Product Type</span>
                      </div>
                      <p className="mt-1 font-medium text-gray-900 dark:text-white">
                        {formData.productType}
                      </p>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                      <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                        <MdCategory className="h-5 w-5" />
                        <span>RCN Type</span>
                      </div>
                      <p className="mt-1 font-medium text-gray-900 dark:text-white">
                        {formData.rcnType || '—'}
                      </p>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                      <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                        <MdDateRange className="h-5 w-5" />
                        <span>Date</span>
                      </div>
                      <p className="mt-1 font-medium text-gray-900 dark:text-white">
                        {new Date(formData.date).toLocaleDateString()}
                      </p>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                      <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                        <MdDateRange className="h-5 w-5" />
                        <span>Julian Date</span>
                      </div>
                      <p className="mt-1 font-medium text-gray-900 dark:text-white">
                        {formData.julianDate}
                      </p>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                      <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                        <MdNumbers className="h-5 w-5" />
                        <span>Quantity</span>
                      </div>
                      <p className="mt-1 font-medium text-gray-900 dark:text-white">
                        {formData.quantity} units
                      </p>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                      <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                        <MdGridOn className="h-5 w-5" />
                        <span>Location</span>
                      </div>
                      <p className="mt-1 font-medium text-gray-900 dark:text-white">
                        {formData.lane}, {formData.slot}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => !isLoading && setShowPreview(false)}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-navy-600 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmSubmit}
                  className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <UsibrasLoader size="small" color="white" />
                  ) : (
                    <>
                      <MdCheck className="mr-2" size={20} />
                      Confirm & Save
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddFinProduct; 